// src/lib/googleMapsLoader.js
let loadingPromise = null;

export function loadGoogleMaps() {
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve, reject) => {
    // Already ready (modern)
    if (window.google?.maps?.importLibrary) return resolve(window.google);

    const key =
      process.env.REACT_APP_GOOGLE_MAPS_KEY || process.env.VITE_GOOGLE_MAPS_KEY;

    if (!key) return reject(new Error("Missing Google Maps API key in env."));

    const SCRIPT_ID = "google-maps-js";
    const CALLBACK_NAME = "__ib__";

    // If a script already exists, just wait for importLibrary
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      // If callback got polluted (Promise/object), Google will throw "c.apply is not a function"
      const d = window.google?.maps;
      if (d && (CALLBACK_NAME in d) && typeof d[CALLBACK_NAME] !== "function") {
        try {
          existing.remove();
        } catch {}
        try {
          delete d[CALLBACK_NAME];
        } catch {
          d[CALLBACK_NAME] = undefined;
        }
        // hard reset so we can re-inject script cleanly
        loadingPromise = null;
        return loadGoogleMaps();
      }

      // Otherwise wait normally
      const t = setInterval(() => {
        if (window.google?.maps?.importLibrary) {
          clearInterval(t);
          resolve(window.google);
        }
      }, 25);

      setTimeout(() => {
        clearInterval(t);
        reject(new Error("Timed out waiting for Google Maps JS API."));
      }, 15000);

      return;
    }

    // Bootstrap dynamic import (Google recommended)
    ((g) => {
      const c = "google";
      const m = document;
      const w = window;

      const root = (w[c] = w[c] || {});
      const d = (root.maps = root.maps || {});
      const r = new Set();
      const e = new URLSearchParams();

      // 🔥 Critical fix:
      // If google.maps.__ib__ exists but is NOT a function, delete it.
      // This is what causes "c.apply is not a function" in Google's runtime.
      if (CALLBACK_NAME in d && typeof d[CALLBACK_NAME] !== "function") {
        try {
          delete d[CALLBACK_NAME];
        } catch {
          d[CALLBACK_NAME] = undefined;
        }
      }

      const loadScriptOnce = () =>
        d[CALLBACK_NAME] ||
        (d[CALLBACK_NAME] = new Promise((resolveCb, rejectCb) => {
          const s = m.createElement("script");
          s.id = SCRIPT_ID;

          // Tell Google what libraries we want (dynamic import will add later too)
          e.set("libraries", [...r].join(","));

          // Set params
          for (const k in g) {
            e.set(k.replace(/[A-Z]/g, (t) => "_" + t[0].toLowerCase()), g[k]);
          }

          // ✅ IMPORTANT: callback must be a FUNCTION at google.maps.__ib__
          // Set it before the script runs.
          d[CALLBACK_NAME] = () => resolveCb(true);

          e.set("callback", `${c}.maps.${CALLBACK_NAME}`);
          s.src = `https://maps.${c}apis.com/maps/api/js?` + e.toString();
          s.async = true;
          s.onerror = () => rejectCb(new Error("Google Maps JS API failed to load."));

          m.head.appendChild(s);
        }));

      d.importLibrary = (lib, ...args) =>
        r.add(lib) && loadScriptOnce().then(() => d.importLibrary(lib, ...args));
    })({
      key,
      v: "weekly",
    });

    const t = setInterval(() => {
      if (window.google?.maps?.importLibrary) {
        clearInterval(t);
        resolve(window.google);
      }
    }, 25);

    setTimeout(() => {
      clearInterval(t);
      reject(new Error("Timed out loading Google Maps JS API."));
    }, 15000);
  });

  return loadingPromise;
}